import React, { useContext } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Modal from '@material-ui/core/Modal';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { UserSessionContext } from 'components/UserSession';
import { guessDevice } from 'utils/browserUtils';
import { FETCH_ASSETS_URL } from 'appenv';

const ChromeIcon = `${FETCH_ASSETS_URL}/assets/browser/chrome.png`;
const FirefoxIcon = `${FETCH_ASSETS_URL}/assets/browser/firefox.png`;
const SafariIcon = `${FETCH_ASSETS_URL}/assets/browser/safari.png`;

const RootContainer = styled.div`
  padding: 12px;
  background-color: #FFF;
  width: 550px;
  height: fit-content;
  position: relative;
  border-radius: 12px;
  outline: 0;
  text-align: center;
`;

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '2000 !important' as any,
  },
  close: {
    position: 'absolute',
    right: '12px',
    top: '0px',
  },
}));

const ButtonGroupContainer = styled.div<{ isDesktop: boolean }>`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  font-size: 16px;
  flex-direction: ${({ isDesktop }) => (isDesktop ? 'row' : 'column')};
`;

const ButtonContainer = styled.div<{ isDesktop: boolean; isIos?: boolean }>`
  display: flex;
  margin: 10px 20px;
  flex-direction: ${({ isDesktop }) => (isDesktop ? 'column' : 'row')};
  text-align: center;
  align-items: center;
  cursor: pointer;
  border: ${({ isIos }) => (isIos ? 'none' : '1px solid #484848')};
  padding: 8px;
  justify-content: center;
`;

const StyledIcon = styled.img`
  width: 40px;
  height: 40px;
`;

const ClickableText = styled.span`
  cursor: pointer;
  margin: 0 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const BrowserText = styled.div`
  margin: 0 10px;
`;

const BrowserDownloadLink = {
  chrome: {
    global: {
      Desktop: 'https://www.google.com/chrome/',
      Android: 'https://play.google.com/store/apps/details?id=com.android.chrome',
    },
    CN: {
      Desktop: 'https://www.google.cn/chrome/',
      Android: 'https://sj.qq.com/myapp/detail.htm?apkName=com.android.chrome',
    },
  },
  firefox: {
    global: {
      Desktop: 'https://www.mozilla.org/en-US/firefox/new/',
      Android: 'https://play.google.com/store/apps/details?id=org.mozilla.firefox',
    },
    CN: {
      Desktop: 'https://www.firefox.com.cn/',
      Android: 'https://www.firefox.com.cn/',
    },
  },
};

const UnsupportedBrowserWarning = (
  {
    rtcCallingStatus,
    close,
    rtcCallingInfo,
    isDesktop,
  }: {
    rtcCallingStatus: string;
    close: () => void;
    rtcCallingInfo: any;
    isDesktop: boolean;
  },
) => {
  const classes = useStyles();
  const { userSession } = useContext(UserSessionContext);
  const { t } = useTranslation();
  const location = useSelector((state: any) => state.location);

  const renderHeaderText = () => {
    let type = 'caller';
    switch (rtcCallingStatus) {
      case 'other_side_browser_not_support':
        type = 'other_side';
        break;
      case 'callee_browser_not_support':
        type = 'callee';
        break;
      default:
        break;
    }
    return t(`call.browser_not_support.${type}.header`, { name: rtcCallingInfo.name });
  };

  const oepnBrowserDownload = (name: string, device: string) => {
    const loc = location === 'CN' ? location : 'global';
    window.open(BrowserDownloadLink[name][loc][device], '_blank');
  };

  const onHelpBtnClick = () => {
    window.trackingEventToFirebase(userSession, 'MainMenu_Click', 'Help', '');
    const { $zopim } = window;
    if (!$zopim) return;
    $zopim(() => {
      $zopim.livechat.setName(userSession?.fullName || '');
      $zopim.livechat.window.show();
    });
  };

  return (
    <Modal
      open
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      className={classes.modal}
    >
      <RootContainer>
        <IconButton aria-label="delete" onClick={close} className={classes.close}>
          <CloseIcon />
        </IconButton>
        <h2>
          {renderHeaderText()}
        </h2>
        <div>
          {
            rtcCallingStatus === 'other_side_browser_not_support' ? (
              <h2>
                {t('call.browser_not_support.other_side.detail', { name: rtcCallingInfo.name })}
              </h2>
            ) : (
              <div>
                {t('call.browser_not_support.hint')}
              </div>
            )
          }
        </div>
        {
          rtcCallingStatus !== 'other_side_browser_not_support' && (
            <>
              <ButtonGroupContainer isDesktop={isDesktop}>
                {
                  guessDevice() !== 'IOS' ? (
                    <>
                      <ButtonContainer onClick={() => oepnBrowserDownload('chrome', guessDevice())} isDesktop={isDesktop}>
                        <StyledIcon src={ChromeIcon} />
                        <BrowserText>Chrome</BrowserText>
                      </ButtonContainer>
                      <ButtonContainer onClick={() => oepnBrowserDownload('firefox', guessDevice())} isDesktop={isDesktop}>
                        <StyledIcon src={FirefoxIcon} />
                        <BrowserText>Firefox</BrowserText>
                      </ButtonContainer>
                    </>
                  ) : (
                    <ButtonContainer isDesktop={isDesktop} isIos>
                      <StyledIcon src={SafariIcon} />
                      <BrowserText>Safari</BrowserText>
                    </ButtonContainer>
                  )
                }
              </ButtonGroupContainer>
              <Trans
                i18nKey="call.browser_not_support.help_message"
                components={{ a: <ClickableText onClick={onHelpBtnClick} /> }}
              />
            </>
          )
        }
      </RootContainer>
    </Modal>
  );
};

export default UnsupportedBrowserWarning;
